/**
 * @module AuroraCart
 * @description Singleton module that manages Aurora Cart.
 */
const AuroraCart = (function () {
  /**
   * An object representing the module's settings.
   *
   * @private
   * @type {object} settings
   * @description The initial settings for the singleton object.
   */
  const SETTINGS = {
    name: "aurora-cart.js",
    debugging: true,
  };

  /**
   * Tag mappings for the module.
   *
   * @type {Object}
   */
  const TAG_MAPPINGS = {
    beveragesRtd: "subcategory::beverages_rtd",
  };

  /**
   * Limits for the module.
   *
   * @type {Object}
   */
  const LIMITS = {
    beveragesRtd: 12,
  };

  /**
   * Enable logging for the module.
   *
   * @type {boolean}
   */
  const LOGGING = true;

  /**
   * Target HTML elements.
   *
   * Cached collection of HTML elements to be
   * used in the module.
   *
   * @type {HTMLElement}
   */
  const ELEMENTS = {
    cart: document.getElementById("cart"),
    cartDrawer: document.getElementById("cart-drawer"),
    cartLineItems: document.getElementById("cart-line-items"),
    lineItem: {
      inputQty: document.querySelectorAll(".cart-line-items input[type='number']") || [],
    },
    button: {
      cartDrawerOpen: document.querySelector("button.js--open-cart-drawer"),
      checkout: document.querySelector("button[id='checkout']"),
      directBilling: document.querySelectorAll("button.js--direct-billing") || [],
      directBillingNo: document.querySelectorAll("button.js--direct-billing[name='no']") || [],
      directBillingYes: document.querySelectorAll("button.js--direct-billing[name='yes']") || [],
    },
    input: {
      termsConditions: document.querySelector("input[name='attributes[termsConditions]']") || false,
    },
  };

  const selectors = {
    stepper: "js--cart-line-item-stepper",
    button: {
      cartItemRemove: ".js--cart-line-item-remove",
      cartItemStepper: ".js--cart-line-item-stepper-button",
      cartComponentsToggle: ".js--cart-components-toggle",
      addToCart: ".js--add-to-cart",
    },
    lineItem: ".js--cart-line-item",
    productCard: ".js--product-card",
  };

  const inputTermsConditionsChecked = function () {
    return ELEMENTS.input?.termsConditions?.checked || false;
  };

  /**
   * Get the order limit from a line item.
   *
   * @param {object} lineItem - The line item to check.
   * @param {Object<string, {item_components: Object[], order_limit: number | null}>} cartAdditionalData - Additional cart data keyed by line item id.
   * @param {Object} options - Function options.
   * @param {boolean} options.fallbackToInventory - Whether to fallback to variant inventory when no order limit is found.
   * @returns {number} The order limit from a line item.
   *
   * @author Brenden Mersey
   */
  const getOrderLimitFromLineItem = (lineItem = {}, cartAdditionalData = {}, options = {}) => {
    const { fallbackToInventory = true } = options;
    const lineItemAdditionalData = cartAdditionalData?.[lineItem?.id] || {};
    const orderLimit = AuroraUtils.getResolvedOrderLimitFromLineItem(lineItem, lineItemAdditionalData, { fallbackToInventory });

    if (orderLimit <= 0) {
      LOGGING && console.log("[ getOrderLimitFromLineItem() ] :: line item order limit missing.");
      return 0;
    }

    LOGGING &&
      console.log("[ getOrderLimitFromLineItem() ] :: line item order limit...", {
        orderLimit,
        lineItemId: lineItem?.id,
        fallbackToInventory,
      });

    return orderLimit;
  };

  /**
   * Check if the line item has an order limit.
   *
   * @param {object} lineItem - The line item to check.
   * @param {Object<string, {item_components: Object[], order_limit: number | null}>} cartAdditionalData - Additional cart data keyed by line item id.
   * @returns {boolean} True if the line item has an order limit, false otherwise.
   *
   * @author Brenden Mersey
   */
  const hasOrderLimit = (lineItem = {}, cartAdditionalData = {}) => {
    return getOrderLimitFromLineItem(lineItem, cartAdditionalData, { fallbackToInventory: false }) > 0;
  };

  const disableLineItemsUsability = function (disabled = true) {
    (document.querySelectorAll(".js--cart-line-items") || []).forEach((el) => {
      el.disabled = disabled ? true : false;
    });
  };

  /**
   * Disable the checkout button.
   *
   * @param {boolean} disabled - Whether to disable the checkout button.
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  const disableCheckoutButton = function (disabled = true) {
    const checkoutButton = ELEMENTS?.button?.checkout;

    if (!checkoutButton) {
      console.warn("[ disableCheckoutButton() ] :: Warning :: Checkout button element not found.");
      return;
    }

    checkoutButton.disabled = disabled;
  };

  /**
   * Get the cart items with order limits.
   *
   * Includes items with an individual per-SKU order limit (_order_limit metafield)
   * as well as items with a tag-based category limit (TAG_MAPPINGS / LIMITS).
   *
   * @param {Object[]} cartItems - The cart items to check.
   * @param {Object<string, {item_components: Object[], order_limit: number | null}>} cartAdditionalData - Additional cart data keyed by line item id.
   * @returns {Object[]} The cart items with order limits or an empty array if no order limits are found.
   *
   * @author Brenden Mersey
   */
  function getCartItemsWithOrderLimits(cartItems = [], cartAdditionalData = {}) {
    return cartItems.filter((lineItem) => hasOrderLimit(lineItem, cartAdditionalData) || hasTagLimit(lineItem));
  }

  const getTagsFromLineItem = (lineItem = {}) => {
    const rawTags = lineItem?.properties?._product_tags ?? "";

    if (!rawTags) {
      return [];
    }

    return rawTags.split(", ").filter(Boolean);
  };

  /**
   * Get a lookup of tag limits.
   *
   * @returns {Object} A lookup of tag limits.
   *
   * @author Brenden Mersey
   */
  const getTagLimitsLookup = () => {
    return Object.entries(TAG_MAPPINGS).reduce((lookup, [limitKey, tagValue]) => {
      const tagLimit = LIMITS[limitKey];
      if (tagLimit !== undefined) {
        lookup[tagValue] = tagLimit;
      }
      return lookup;
    }, {});
  };

  /**
   * Check if the line item has a tag-based order limit.
   *
   * @param {object} lineItem - The line item to check.
   * @returns {boolean} True if any of the line item's product tags match a limit in the tag limits lookup, false otherwise.
   *
   * @author Brenden Mersey
   */
  const hasTagLimit = (lineItem = {}) => {
    const tags = getTagsFromLineItem(lineItem);
    const tagLimitsLookup = getTagLimitsLookup();
    return tags.some((tag) => tagLimitsLookup[tag] !== undefined);
  };

  /**
   * Get the product limit from a tag.
   *
   * @param {string} tag - The tag to get the product limit from.
   * @returns {number} The product limit from a tag.
   *
   * @author Brenden Mersey
   */
  function getProductLimitFromTag(tag = "") {
    // First get the clean property name from the mapping
    const tagKey = Object.keys(TAG_MAPPINGS).find((key) => TAG_MAPPINGS[key] === tag);

    // Then use that to get the value from LIMITS, or 0 if no limit is found
    const tagLimit = LIMITS[tagKey] ?? 0;

    // log tag key and associated limit
    LOGGING && console.log("[ getProductLimitFromTag() ] :: tag, tagKey, and associated limit...", { tag, tagKey, tagLimit });

    // return the tag limit
    return tagLimit;
  }

  /**
   * Get the total grams from the cart items.
   *
   * @param {Object[]} cartItems - The cart items to get the total grams from.
   * @returns {number} The total grams from the cart items.
   *
   * @author Brenden Mersey
   */

  const getTotalGramsFromCartItems = function (cartItems = [], cartAdditionalData = {}) {
    // exit early if the cart items are not an array
    if (!Array.isArray(cartItems)) {
      console.warn("[ getTotalGramsFromCartItems() ] :: Warning :: Expected array, got:", typeof cartItems);
      return 0;
    }

    // set the precision for the total grams
    const GRAM_PRECISION = 2;

    const toGramContribution = (equivalencyRaw, quantityRaw) => {
      const equivalency = Number(equivalencyRaw);
      const quantity = Number(quantityRaw);
      const isValid = Number.isFinite(equivalency) && Number.isFinite(quantity) && equivalency >= 0 && quantity >= 0;

      return isValid ? equivalency * quantity : 0;
    };

    // reduce the cart items to total grams by multiplying the equivalency by the quantity
    const total = cartItems.reduce((sum, lineItem) => {
      const lineItemAdditionalData = cartAdditionalData[lineItem.id] || {};
      const itemComponentsRaw = lineItemAdditionalData?.item_components;
      const isBundle = Array.isArray(itemComponentsRaw) && itemComponentsRaw.length > 0;

      if (isBundle) {
        const itemComponents = itemComponentsRaw;

        const bundleTotal = itemComponents.reduce((componentSum, component) => {
          if (!component) {
            return componentSum;
          }

          const eqRaw = component?.properties?._equivalency ?? 0;
          // component.quantity is already the total across all bundle instances —
          // the Liquid cart endpoint pre-scales it by the parent line quantity.
          const qtyRaw = component?.quantity ?? 0;

          return componentSum + toGramContribution(eqRaw, qtyRaw);
        }, 0);

        return sum + bundleTotal;
      }

      const eqRaw = lineItem?.properties?._equivalency ?? 0;
      const qtyRaw = lineItem?.quantity ?? 0;

      return sum + toGramContribution(eqRaw, qtyRaw);
    }, 0);

    // Return as number with 2 decimal precision
    return Number(total.toFixed(GRAM_PRECISION));
  };

  const handleInputChangeEvents = async function (event) {
    // prevent any immediate input changes while this service runs
    disableLineItemsUsability(true);

    const lineItem = event.target.closest(selectors.lineItem);
    const lineItemKey = lineItem.getAttribute("data-key");
    const lineItemProductHandle = lineItem.getAttribute("data-product-handle");
    const lineItemQty = parseInt(event.target.value);
    // const product = await AuroraServices.getProductByHandle(lineItemProductHandle);
    // fetch inventory level of products?
    updateCartLineItemByKey(lineItemKey, lineItemQty);
  };

  const debouncedInputHandler = AuroraUtils.debounce(handleInputChangeEvents, 350);

  const registerEventListenerOnCartLineItems = function () {
    document.querySelectorAll(".cart-line-items input[type='number']").forEach((input) => {
      input.removeEventListener("change", debouncedInputHandler);
      input.addEventListener("change", debouncedInputHandler);
    });
  };

  const onClickCloseCartDrawer = () => {
    if (ELEMENTS.cartDrawer) {
      document.body.addEventListener("click", (event) => {
        // let drawer = event.target.closest(".cart-drawer") ? true : false;
        // let buttonDrawerOpen = event.target.closest(".js--open-cart-drawer") ? true : false;
        let buttonDrawerClose = event.target.closest(".js--close-cart-drawer") ? true : false;
        // if ((!drawer && !buttonDrawerOpen) || buttonDrawerClose) {
        if (buttonDrawerClose) {
          document.body.classList.remove("cart-drawer--open");
          ELEMENTS.cartDrawer.classList.remove("open");
        }
      });
    }
  };

  const onClickInputTermsConditions = function () {
    if (ELEMENTS.input.termsConditions) {
      ELEMENTS.input.termsConditions.addEventListener(
        "click",
        AuroraUtils.debounce((event) => {
          if (!event.target.checked) {
            disableCheckoutButton(true);
          } else {
            const termsConditionsChangeEvent = new CustomEvent("aurora::termsConditionsChanged");
            document.dispatchEvent(termsConditionsChangeEvent);
          }
        }, 100)
      );
    }
  };

  const onClickOpenCartDrawer = () => {
    if (ELEMENTS.button.cartDrawerOpen) {
      ELEMENTS.button.cartDrawerOpen.addEventListener("click", () => {
        document.body.classList.add("cart-drawer--open");
        ELEMENTS.cartDrawer.classList.add("open");
      });
    }
  };

  const onClickRemoveCartLineItem = function () {
    document.addEventListener("click", (event) => {
      if (event.target.closest(selectors.button.cartItemRemove)) {
        const lineItem = event.target.closest(selectors.lineItem);
        const lineItemKey = lineItem.getAttribute("data-key");
        disableLineItemsUsability(true);
        updateCartLineItemByKey(lineItemKey, 0, lineItem);
      }
    });
  };

  const onClickSetDirectBillingPreference = function () {
    ELEMENTS.button.directBilling.forEach((button) => {
      button.addEventListener("click", () => {
        ELEMENTS.button.directBilling.forEach((button) => button.classList.remove("selected"));
        AuroraServices.updateCartAttribute({
          insurance:
            button.name === "yes"
              ? JSON.stringify({
                  insurance_coverage_uuid: AuroraCustomer.info.insurance_coverage_uuid,
                  client_id: AuroraCustomer.info.client_id,
                })
              : "",
        });
        if ("yes" === button.name) {
          ELEMENTS.button.directBillingYes.forEach((button) => button.classList.add("selected"));
        } else {
          ELEMENTS.button.directBillingNo.forEach((button) => button.classList.add("selected"));
        }
      });
    });
  };

  const onClickUpdateCartLineItemQuantity = function () {
    document.addEventListener("click", (event) => {
      if (event.target.closest(selectors.button.cartItemStepper)) {
        const lineItemStepperButton = event.target.closest(selectors.button.cartItemStepper);
        const lineItemStepperButtonType = lineItemStepperButton.classList.contains("increase") ? "increase" : "decrease";
        const lineItem = lineItemStepperButton.closest(selectors.lineItem);
        const lineItemStepperButtonDecrease = lineItem.querySelector(".js--cart-line-item-stepper-button.decrease");
        const lineItemStepperButtonIncrease = lineItem.querySelector(".js--cart-line-item-stepper-button.increase");
        const lineItemQtyInput = lineItem.querySelector("input[type='number']");

        if (lineItemStepperButton && lineItemQtyInput) {
          const min = parseInt(lineItemQtyInput.min) || 0;
          const max = parseInt(lineItemQtyInput.max) || 9999;
          let quantity = parseInt(lineItemQtyInput.value);
          let prevQuantity = parseInt(lineItemQtyInput.value);

          if ("increase" === lineItemStepperButtonType) {
            if (quantity < max) {
              lineItemStepperButtonIncrease.disabled = false;
              quantity++;
            } else {
              lineItemStepperButtonIncrease.disabled = true;
            }
          } else {
            if (quantity > min) {
              lineItemStepperButtonIncrease.disabled = false;
              quantity--;
            } else {
              lineItemStepperButtonDecrease.disabled = true;
            }
          }

          // update line item input with quantity
          lineItemQtyInput.value = quantity;

          // if quantity has changed, dispatch "change" event to line item input
          if (quantity != prevQuantity) {
            const stepperQuantityChangeEvent = new CustomEvent("change");
            lineItemQtyInput.dispatchEvent(stepperQuantityChangeEvent);
          }
        }
      }
    });
  };

  const onClickToggleCartLineItemComponents = function () {
    document.addEventListener("click", (event) => {
      const toggle = event.target.closest(selectors.button.cartComponentsToggle);
      if (!toggle) return;

      const containerId = toggle.getAttribute("aria-controls");
      const container =
        (containerId && document.getElementById(containerId)) ||
        toggle.closest(".cart-line-item__components")?.querySelector(".cart-line-item__components-container");

      if (!container) return;

      const isExpanded = toggle.getAttribute("aria-expanded") === "true";
      toggle.setAttribute("aria-expanded", isExpanded ? "false" : "true");
      container.hidden = isExpanded;

      const label = toggle.querySelector(".cart-line-item__components-toggle-text");
      if (label) {
        const nextText = isExpanded ? label.getAttribute("data-show-text") : label.getAttribute("data-hide-text");
        if (nextText) {
          label.textContent = nextText;
        }
      }
    });
  };

  const initCartAddToWishlist = function () {
    window.SwymCallbacks = window.SwymCallbacks || [];
    window.SwymCallbacks.push(function (swat) {
      if (!swat || typeof swat.initializeActionButtons !== "function") return;
      swat.initializeActionButtons("#cart-line-items");
    });
  };

  /**
   * Debounced quantity input handler:
   * Tracks individual timers per input using a WeakMap,
   * validates the value after user stops typing (300ms),
   * and clamps it to the input's max attribute.
   */
  const onStepperUpdate = function () {
    const timers = new WeakMap();
    document.addEventListener("input", (event) => {
      const stepperInput = event.target;
      if (!stepperInput.matches('input[type="number"].cart-line-item__stepper-input')) return;
      clearTimeout(timers.get(stepperInput));
      const timer = setTimeout(() => {
        const value = Number(stepperInput.value);
        const max = Number(stepperInput.max) || Infinity;
        if (value > max) {
          stepperInput.value = max;
        }
      }, 300);
      timers.set(stepperInput, timer);
    });
  };

  /**
   * Check if the cart items are within the order limit.
   *
   * @param {Object[]} cartItemsWithOrderLimits - The cart items array with line items who have an order limit to check if within the order limit.
   * @param {Object<string, {item_components: Object[], order_limit: number | null}>} cartAdditionalData - Additional cart data keyed by line item id.
   * @returns {boolean} True if the cart items are within the order limit, false otherwise.
   *
   * @author Brenden Mersey
   */
  const itemsWithinOrderLimit = function (cartItemsWithOrderLimits = [], cartAdditionalData = {}) {
    if (!Array.isArray(cartItemsWithOrderLimits) || cartItemsWithOrderLimits.length === 0) {
      LOGGING && console.log("[ itemsWithinOrderLimit() ] :: No cart items with order limits detected.");
      return true;
    }

    LOGGING &&
      console.log("[ itemsWithinOrderLimit() ] :: init :: cart items with order limits...", {
        cartItemsWithOrderLimits,
      });

    const aggregatedTagTotals = {};
    const tagLimitsLookup = getTagLimitsLookup();

    for (const lineItem of cartItemsWithOrderLimits) {
      const quantityRaw = lineItem?.quantity ?? 0;
      const quantity = Number(quantityRaw);
      const title = lineItem?.product_title ?? "title not set";

      if (!Number.isFinite(quantity) || quantity <= 0) {
        continue;
      }

      const orderLimit = getOrderLimitFromLineItem(lineItem, cartAdditionalData, { fallbackToInventory: true });

      if (orderLimit > 0 && quantity > orderLimit) {
        LOGGING &&
          console.warn(`[ itemsWithinOrderLimit() ] :: line item '${title}' over individual limit.`, {
            lineItem,
            quantity,
            orderLimit,
          });
        return false;
      }

      const tags = getTagsFromLineItem(lineItem);

      for (const tag of tags) {
        const tagLimit = tagLimitsLookup[tag];

        if (tagLimit === undefined) {
          continue;
        }

        aggregatedTagTotals[tag] = (aggregatedTagTotals[tag] ?? 0) + quantity;

        if (aggregatedTagTotals[tag] > tagLimit) {
          LOGGING &&
            console.warn("[ itemsWithinOrderLimit() ] :: tag aggregate over limit.", {
              tag,
              tagLimit,
              aggregatedTotal: aggregatedTagTotals[tag],
              lineItem,
            });
          return false;
        }
      }
    }

    LOGGING &&
      console.log("[ itemsWithinOrderLimit() ] :: complete :: cart items within order limit.", {
        aggregatedTagTotals,
      });

    return true;
  };

  const updateCartLineItemByKey = async function (key = "", quantity = 1) {
    // Get the current cart items
    const cart = await AuroraServices.getCart();
    // Find the cart item with the matching key
    let targetCartItem = cart.items.find((cartItem) => cartItem.key === key);

    if (targetCartItem) {
      targetCartItem.quantity = quantity;

      await AuroraServices.changeCartByLineItemKey(
        key,
        quantity,
        {
          ...targetCartItem.properties,
        },
        () => disableLineItemsUsability(false)
      );
    } else {
      console.log(`Cart item with key ${key} is not found`);
    }
  };

  const checkSameDayShipping = async function (postalCode = "") {
    LOGGING && console.log("[ checkSameDayShipping() ] :: postal code...", postalCode);
    if (typeof postalCode === "string" && postalCode) {
      try {
        const fetchReq = await fetch(`/apps/api/client/shipping_options/${postalCode?.trim()?.replace(/\s+/g, "")}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        });
        const fetchRes = await fetchReq.json();
        LOGGING && console.log("[ checkSameDayShipping() ] :: fetchRes...", fetchRes);
        if (fetchRes?.available_shipping_methods?.includes("SameDayGTA")) {
          return true;
        }
      } catch (err) {
        console.error("Error", err);
      }
    }

    return false;
  };

  const claimCartAttributes = function (customer = {}, cart = {}) {
    const updatedAttributes = {};

    /* Create 'cart_id' attribute for Veteran and BPCA customers
       if there's no the attribute or expired
     */
    if (parseFloat(customer?.insurance_remaining_coverage) > 0 || customer?.insurance_direct_billing === "True") {
      const insuranceClaimExpireTimestamp =
        parseInt(cart?.attributes?.insurance_expire_timestamp) || parseInt(cart?.attributes?.vac_claim_expire_timestamp);

      if (!cart?.attributes?.cart_id || Date.now() > insuranceClaimExpireTimestamp) {
        updatedAttributes.cart_id = `${customer?.client_id}-${new Date().getTime()}`;
        updatedAttributes.vac_claim_expire_timestamp = null;
        updatedAttributes.insurance_expire_timestamp = null;
      }
    }

    if (SETTINGS.debugging) {
      console.log("[ claimCartAttributes() ] :: updated attributes...", updatedAttributes);
    }

    return updatedAttributes;
  };

  const initCartAttributes = async function (customer = {}, cart = {}) {
    if (SETTINGS.debugging) {
      console.log("[ async updateCartAttributes() ] :: [ async initCartAttributes() ] :: customer and cart...", {
        customer,
        cart,
      });
    }

    // get cart attributes from local storage
    const accHash = localStorage.getItem("accHash");
    const clientCareId = localStorage.getItem("agentId");

    // sanitize the address object, swapping null values for an empty string
    const address = AuroraUtils.swapNullForValue(customer.shopify_address, "");
    // get the postal code from the address object
    const addressPostalCode = address?.zip ?? "";
    // convert the entire address object to a string
    const addressString = JSON.stringify(address);

    // define cart attributes
    const attributes = {
      address: addressString,
      cart_id: cart?.attributes?.cart_id || `${customer?.client_id}-${new Date().getTime()}`,
      first_sell_date: customer.first_sell_date,
      hcp_first_name: customer.hcp_first_name,
      hcp_last_name: customer.hcp_last_name,
      hcp_license_number: customer.hcp_license_number,
      hcp_licensing_province: customer.hcp_licensing_province,
      insurance:
        customer.insurance_direct_billing === "True"
          ? JSON.stringify({
              insurance_coverage_uuid: customer.insurance_coverage_uuid,
              client_id: customer.client_id,
            })
          : "",
      is_patient: "",
      is_shop_as: "false",
      is_veteran: customer.is_veteran,
      LRTokenKey: customer.LRTokenKey,
      med_dc: customer.location, // could be wrong
      med_doc_id: customer.med_doc_id,
      same_day_gta: await checkSameDayShipping(addressPostalCode),
      vac_price_per_gram: customer.vac_price_per_gram,
    };

    if (clientCareId) {
      attributes["client_care_id"] = clientCareId;
    }

    if (accHash) {
      attributes["acc_hash"] = accHash;
      const isShopAs = await AuroraServices.checkIfCareGiverClient(accHash);
      attributes.is_shop_as = isShopAs.toString();
    }

    if (SETTINGS.debugging) {
      console.log("[ async updateCartAttributes() ] :: [ async initCartAttributes() ] :: attributes...", attributes);
    }

    return attributes;
  };

  /**
   * Updates the cart attributes.
   *
   * @async
   * @function updateCartAttributes
   * @description This function updates the cart attributes.
   * @returns {void} This function does not return anything.
   *
   * @author Halina Tretsiakova
   * @author Brenden Mersey
   */
  const updateCartAttributes = async function (customer = {}, cart = {}, cartAdditionalData = {}) {
    console.log("[ async updateCartAttributes() ] :: initialized :: cart and customer...", { cart, customer });

    // if the customer is not logged in, return
    if (!customer.is_logged_in) {
      // log if the customer is not logged in
      console.log("[ async updateCartAttributes() ] :: customer is not logged in, exiting...", { cart, customer });
      return;
    }

    // set cart attributes
    const attributes = await initCartAttributes(customer, cart);

    // set terms and conditions attribute
    attributes.accepted_terms = inputTermsConditionsChecked();

    // set total gram count from cart items attribute
    attributes.gram_count = getTotalGramsFromCartItems(cart.items, cartAdditionalData);

    // log attributes before combining claim attributes
    console.log("[ async updateCartAttributes() ] :: before combining claim attributes...", attributes);

    // combine claim attributes with attributes
    Object.assign(attributes, claimCartAttributes(customer, cart));

    // log attributes after combining claim attributes
    console.log("[ async updateCartAttributes() ] :: after combining claim attributes...", attributes);

    // update cart attributes
    const updatedCart = await AuroraServices.updateCartAttribute(attributes);

    // log attributes after updating cart with new attributes
    console.log("[ async updateCartAttributes() ] :: completed :: updated cart with new attributes...", updatedCart);

    // if the debugger is enabled, print cart attributes
    if (window?.theme?.log?.customer) {
      (document.querySelectorAll(".js--debugger-customer-cart-info") || []).forEach((el) => AuroraRender.cartAttributesFromCart(updatedCart, el));
    }
  };

  function getBundleConsistency(cart = {}) {
    function getQuantityMap(cart) {
      const quantityMap = new Map();
      cart.items.forEach((item) => {
        quantityMap.set(item.id, item.quantity);
      });
      return quantityMap;
    }

    function isGiftBundle(item) {
      return Boolean(item?.properties?._is_bundle_gift);
    }

    function parseRequiredItems(properties) {
      const rawRequiredItems = properties?._requiredItems;
      if (!rawRequiredItems) return [];

      // Handle both array and string representations
      if (Array.isArray(rawRequiredItems)) return rawRequiredItems;
      if (typeof rawRequiredItems === "string") {
        try {
          return JSON.parse(rawRequiredItems);
        } catch (_) {
          return [];
        }
      }
      return [];
    }

    if (!cart || !cart.items || cart.items.length === 0) {
      console.warn("Cart is empty or not initialized");
      return;
    }

    const quantityById = getQuantityMap(cart);

    const giftBundles = cart.items.filter(isGiftBundle).map((giftItem) => {
      const giftQuantity = Number(giftItem.quantity || 0);
      const requiredItems = parseRequiredItems(giftItem.properties);
      const missingItems = [];
      const extraItems = [];

      let maxPossibleGifts = Infinity;

      for (const required of requiredItems) {
        const requiredId = Number(required.id);
        const requiredPerGift = Number(required.quantity || 0);
        const totalRequired = requiredPerGift * giftQuantity;
        const presentQuantity = Number(quantityById.get(requiredId) || 0);

        // Calculate how many gifts this item can support
        if (requiredPerGift > 0) {
          const possibleGiftsFromThisItem = Math.floor(presentQuantity / requiredPerGift);
          maxPossibleGifts = Math.min(maxPossibleGifts, possibleGiftsFromThisItem);
        }

        if (presentQuantity < totalRequired) {
          missingItems.push({
            id: requiredId,
            requiredQty: totalRequired,
            presentQty: presentQuantity,
          });
        } else if (presentQuantity > totalRequired) {
          extraItems.push({
            id: requiredId,
            requiredQty: totalRequired,
            presentQty: presentQuantity,
          });
        }
      }

      // Determine the action needed
      let action = "satisfied";
      if (missingItems.length > 0) {
        action = "missing";
      } else if (maxPossibleGifts > giftQuantity) {
        action = "addGift";
      }

      return {
        giftId: Number(giftItem.id),
        key: giftItem.key,
        giftQty: giftQuantity,
        maxPossibleGifts: maxPossibleGifts === Infinity ? 0 : maxPossibleGifts,
        missing: missingItems,
        extra: extraItems,
        action: action,
      };
    });

    const hasGiftBundles = giftBundles.length > 0;
    const allGiftsSatisfied = giftBundles.every((bundle) => bundle.missing.length === 0 && bundle.action === "satisfied");

    return { isConsistent: allGiftsSatisfied, hasGift: hasGiftBundles, gifts: giftBundles };
  }

  const checkBundleConsistency = function (cart = {}) {
    const bundleConsistency = getBundleConsistency(cart);
    if (!bundleConsistency) return false;
    const { isConsistent, hasGift, gifts } = bundleConsistency;
    if (isConsistent || !hasGift) {
      return false;
    }

    for (const gift of gifts) {
      const action = gift.action;
      if (action !== "satisfied") {
        updateCartLineItemByKey(gift.key, gift.maxPossibleGifts);
        return true; // Only process the first inconsistent gift bundle to avoid multiple updates
      }
    }
    return false;
  };

  /**
   * Initializes the Aurora Cart singleton object.
   *
   * @async
   * @function init
   * @description This function initializes the Aurora Cart singleton object, including
   * event listeners and other necessary components.
   * @returns {void} This function does not return anything.
   *
   * @author Brenden Mersey
   */
  const init = async function () {
    LOGGING && console.log(`${SETTINGS.name} :: Start`);

    onClickCloseCartDrawer();
    onClickInputTermsConditions();
    onClickOpenCartDrawer();
    onClickRemoveCartLineItem();
    onClickSetDirectBillingPreference();
    onClickUpdateCartLineItemQuantity();
    onClickToggleCartLineItemComponents();
    onStepperUpdate();

    LOGGING && console.log(`${SETTINGS.name} :: Complete`);
  };

  return {
    checkBundleConsistency,
    disableCheckoutButton,
    elements: ELEMENTS,
    getCartItemsWithOrderLimits,
    getOrderLimitFromLineItem,
    hasOrderLimit,
    hasTagLimit,
    getTotalGramsFromCartItems,
    init,
    inputTermsConditionsChecked,
    itemsWithinOrderLimit,
    registerEventListenerOnCartLineItems,
    selectors,
    settings: SETTINGS,
    updateCartAttributes,
    initCartAddToWishlist,
  };
})();
